import { useMemo, useState } from 'react';
import { useProviderSlots, type ProviderSlotsResponse } from '@/services/scheduling/use-provider-slots';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import { ProviderPickerGrid, type SelectedProviderInfo } from '@/scheduling/components/shared/provider-picker-grid';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import { useAuthStore } from '@/auth/auth-store';
import type { BlockActor } from '@/types/block-availability-api';

/**
 * BlockAvailabilityProviderSearch — wires `useProviderSlots` to the
 * shared `ProviderPickerGrid` (same control the create-appointment
 * panel uses).
 *
 * What this component owns:
 *   - Data: fetches the provider list for the selected date via
 *     `useProviderSlots`.
 *   - Filter: client-side substring match on name / speciality
 *     (Flutter parity: `block_availability_widget.dart:407-417`).
 *   - Selection projection: maps `ProviderSlotsResponse` →
 *     `BlockActor` for the outer form state.
 *
 * What `ProviderPickerGrid` owns:
 *   - Visual layout (2-column card grid, COLLAPSED_COUNT=4 chevron
 *     toggle, brand-primary selected card with × clear).
 *   - Loading skeletons + empty states.
 *
 * pageSize defaults to 5 — PROD-825 tracks the backend SQL paging
 * bug that 500s when pageSize > 5 with pageNumber=0.
 */

interface Props {
  organizationId: string;
  practiceId: string;
  selectedDate: Date;
  selectedProvider: BlockActor | null;
  onSelect: (provider: BlockActor) => void;
  onClear: () => void;
  /**
   * Read-only mode (PR 5 edit view). When true, the selected card
   * renders without the × clear button and the search/grid hides
   * (the user can't pick a different provider). Used when the panel
   * shows an existing block.
   */
  disabled?: boolean;
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toActor(p: ProviderSlotsResponse): BlockActor {
  return {
    id: p.id,
    name: p.name || 'Unknown',
    photo: p.photo ?? null,
    // `subtitle` is the picker card's second line for either kind of actor;
    // for a practitioner that is still the speciality (PROD-2454).
    subtitle: specialityDisplay(p.speciality) ?? null,
  };
}

function toSelectedInfo(p: BlockActor): SelectedProviderInfo {
  return { id: p.id, name: p.name, photo: p.photo ?? null, speciality: p.subtitle ?? null };
}

export function BlockAvailabilityProviderSearch({
  organizationId,
  practiceId,
  selectedDate,
  selectedProvider,
  onSelect,
  onClear,
  disabled = false,
}: Props) {
  const [search, setSearch] = useState('');

  const query = useProviderSlots({
    organizationId,
    practiceId,
    date: isoDay(selectedDate),
    searchSlot: false,
    slotCount: 0,
  });

  // PROD-1029 (F-SCH-132 sibling): a doctor/nurse can only block their own
  // availability — self-filter the roster before the search box, mirroring
  // Flutter block_availability_widget.dart:594-605 (removeWhere doctor id /
  // nurse practitionerId). org-admin/staff see the full roster.
  const user = useAuthStore((s) => s.user);
  const providers = useMemo(
    () => filterProvidersForCurrentUser(query.data ?? [], user),
    [query.data, user],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return providers;
    return providers.filter((p) => {
      const name = (p.name ?? '').toLowerCase();
      const spec = (specialityDisplay(p.speciality) ?? '').toLowerCase();
      return name.includes(needle) || spec.includes(needle);
    });
  }, [providers, search]);

  return (
    <ProviderPickerGrid
      providers={filtered}
      selected={selectedProvider ? toSelectedInfo(selectedProvider) : null}
      isLoading={query.isLoading}
      onSelect={(p) => onSelect(toActor(p))}
      onClear={onClear}
      searchValue={search}
      onSearchChange={setSearch}
      readOnly={disabled}
    />
  );
}
